import { State, Action, StateContext, createPropertySelectors } from '@ngxs/store';
import { patch, append, removeItems } from '@ngxs/store/operators';
import { inject, Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs';

import {
    EPlayerEventsMode,
    IEventStateModel,
    IGameEvent,
    IPlayerEvent,
    IPlayerEventShiftsApiResponse
} from './event.models';
import { eventStateDefaults } from './event.utils';
import { EventService } from './event.service';
import {
    AddGameEvent,
    AddPlayerEvent,
    DeleteEvents,
    FetchFlagDefinitions,
    FetchGameEventDefinitions,
    FetchGameEvents,
    FetchPlayerEventDefinitions,
    FetchPlayerEvents,
    FetchShiftEventDefinitions,
    FetchPlayerShiftEvents
} from './event.actions';

import { UpdateQueryParams } from 'src/services/query-params/query-params.actions';

@Injectable()
@State<IEventStateModel>({
    name: eventStateDefaults.sliceName,
    defaults: eventStateDefaults.defaults
})
export class EventState {
    private readonly _eventService = inject(EventService);

    public static selectors = createPropertySelectors<IEventStateModel>(EventState);

    @Action(FetchPlayerEventDefinitions)
    private _fetchPlayerEventDefinitions(ctx: StateContext<IEventStateModel>) {
        return this._eventService.getPlayerEventDefinitionsApi().pipe(
            map(playerEventDefinitions => {
                return ctx.patchState({
                    playerEventDefinitions
                });
            })
        );
    }

    @Action(FetchGameEventDefinitions)
    private _fetchGameEventDefinitions(ctx: StateContext<IEventStateModel>) {
        return this._eventService.getGameEventDefinitionsApi().pipe(
            map(gameEventDefinitions => {
                return ctx.patchState({
                    gameEventDefinitions
                });
            })
        );
    }

    @Action(FetchShiftEventDefinitions)
    private _fetchShiftEventDefinitions(ctx: StateContext<IEventStateModel>) {
        return this._eventService.getShiftEventDefinitionsAPI().pipe(
            map(shiftEventDefinitions => {
                return ctx.patchState({
                    shiftEventDefinitions
                });
            })
        );
    }

    @Action(FetchFlagDefinitions)
    private _fetchFlagDefinitions(ctx: StateContext<IEventStateModel>) {
        return this._eventService.getFlagDefinitionsApi().pipe(
            map(flagDefinitions => {
                return ctx.patchState({
                    flagDefinitions
                });
            })
        );
    }

    @Action(FetchGameEvents)
    private _fetchGameEvents(ctx: StateContext<IEventStateModel>, { params }: FetchGameEvents) {
        return this._eventService.getGameEventsApi(params).pipe(
            map(gameEvents => {
                return ctx.patchState({
                    gameEvents
                });
            })
        );
    }

    @Action(FetchPlayerEvents)
    private _fetchPlayerEvents(ctx: StateContext<IEventStateModel>, { params }: FetchPlayerEvents) {
        return this._eventService.getPlayerEventsApi(params).pipe(
            map((response) => {
                if (params?.playereventid && params.playereventid.length > 0) {
                    return ctx.setState(
                        patch<IEventStateModel>({
                            playerEvents: append<IPlayerEvent>(response)
                        })
                    );
                }

                return ctx.setState(
                    patch<IEventStateModel>({
                        playerEvents: response
                    })
                );
            })
        );
    }

    @Action(FetchPlayerShiftEvents)
    private _fetchShiftEvents(ctx: StateContext<IEventStateModel>, { params }: FetchPlayerShiftEvents) {
        return this._eventService.getPlayerShiftEventsApi(params).pipe(
            map((response) => {
                return ctx.setState(patch<IEventStateModel>({
                    playerShiftEvents: response
                }));
            })
        );
    }

    @Action(AddPlayerEvent)
    private _addPlayerEvent(ctx: StateContext<IEventStateModel>, { gameId, payload }: AddPlayerEvent) {
        return this._eventService.postPlayerEvent(gameId, [payload]).pipe(
            switchMap((response) => {
                return ctx.dispatch([
                    new FetchPlayerEvents({
                        gameid: [gameId],
                        mode: EPlayerEventsMode.RAW,
                        playereventid: [response.eventIds[0]]
                    }),
                    new UpdateQueryParams({ eventid: response.eventIds[0] })
                ]);
            })
        );
    }

    @Action(AddGameEvent)
    private _addGameEvent(ctx: StateContext<IEventStateModel>, { gameId, payload }: AddGameEvent) {
        return this._eventService.postGameEvent(gameId, payload);
    }

    @Action(DeleteEvents)
    private _deleteEvents(ctx: StateContext<IEventStateModel>, { gameEventIds, playerEventIds, shiftEventIds }: DeleteEvents) {
        return this._eventService.deleteEventsApi(gameEventIds, playerEventIds, shiftEventIds).pipe(
            map(() => {
                const gameIds = new Set(gameEventIds);
                const playerIds = new Set(playerEventIds);
                const shiftIds = new Set(shiftEventIds);
                const updates: Parameters<typeof patch<IEventStateModel>>[0] = {};

                if (gameEventIds.length > 0) {
                    updates.gameEvents = removeItems<IGameEvent>((event) => gameIds.has(event.id));
                }
                if (playerEventIds.length > 0) {
                    updates.playerEvents = removeItems<IPlayerEvent>((event) => playerIds.has(event.id));
                }
                if (shiftEventIds.length > 0) {
                    updates.playerShiftEvents = removeItems<IPlayerEventShiftsApiResponse>((event) => shiftIds.has(event.id));
                }

                return ctx.setState(patch<IEventStateModel>(updates));
            })
        );
    }
}
